import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  useTheme,
  alpha,
  Stack,
  Fade,
  Grow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

interface EmptyStateProps {
  onCreateProject: (name: string, description: string) => void;
}

const FeatureCard: React.FC<{ 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  delay: number;
}> = ({ icon, title, description, delay }) => {
  const theme = useTheme();
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay]);
  
  return (
    <Grow in={show} timeout={800} style={{ transformOrigin: '0 0 0' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }
        }}
      >
        <Box 
          sx={{ 
            mr: 2, 
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            p: 1,
            borderRadius: 1.5,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Paper>
    </Grow>
  );
};

const EmptyState: React.FC<EmptyStateProps> = ({ onCreateProject }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [animate, setAnimate] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setProjectName('');
    setProjectDescription('');
  };

  const handleCreate = () => {
    if (projectName.trim()) {
      onCreateProject(projectName.trim(), projectDescription.trim());
      handleClose();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: { xs: 2, md: 4 },
        background: `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.3)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
      }}
    >
      <Fade in={animate} timeout={1000}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            maxWidth: 800,
            width: '100%',
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            },
          }}
        >
          <AssignmentIcon 
            sx={{ 
              fontSize: 60, 
              color: theme.palette.primary.main, 
              mb: 2,
              opacity: 0.9,
            }} 
          />
          
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}
          >
            Welcome to Requirements Tracker
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ 
              maxWidth: '85%', 
              mx: 'auto', 
              mb: 4,
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            Simplify your requirements management with this powerful tool. 
            Track progress, identify gaps, and ensure completeness with AI-powered assistance.
          </Typography>
          
          <Fade in={animate} style={{ transitionDelay: '400ms' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleOpen}
              sx={{ 
                mt: 2, 
                mb: 4,
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: theme.shadows[4],
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              Create New Project
            </Button>
          </Fade>
          
          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Key Features
            </Typography>
          </Box>
          
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={2}
            sx={{ mt: 2 }}
          >
            <FeatureCard
              icon={<CheckCircleOutlineIcon />}
              title="Track Requirements"
              description="Keep track of all your project requirements in one place with status tracking."
              delay={600}
            />
            
            <FeatureCard
              icon={<AutoGraphIcon />}
              title="Monitor Progress"
              description="Visual dashboards help you track project progress and identify areas that need attention."
              delay={800}
            />
            
            <FeatureCard
              icon={<LightbulbIcon />}
              title="AI Assistance"
              description="Get intelligent suggestions for requirements content and completeness."
              delay={1000}
            />
          </Stack>
        </Paper>
      </Fade>

      {/* Create Project Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[10],
          }
        }}
      >
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the details for your new requirements project.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Project Name"
            type="text"
            fullWidth
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            variant="outlined"
            required
            sx={{ mt: 3 }}
          />
          <TextField
            margin="dense"
            id="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!projectName.trim()}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmptyState;
